(() => {
  const controllerKey = '__hematixHomeV3';
  if (window[controllerKey]) {
    window[controllerKey].scan(document);
    return;
  }

  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = value => value * value * (3 - 2 * value);
  const numeric = (element, name, fallback = 0) => {
    const value = Number.parseFloat(element.getAttribute(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const init = root => {
    if (instances.has(root)) return;
    const stage = root.querySelector('[data-hx-v3-stage]');
    const pin = root.querySelector('[data-hx-v3-pin]');
    const products = [...root.querySelectorAll('[data-hx-v3-product]')]
      .sort((a, b) => numeric(a, 'data-hx-v3-product') - numeric(b, 'data-hx-v3-product'));
    const copies = [...root.querySelectorAll('[data-hx-v3-copy]')]
      .sort((a, b) => numeric(a, 'data-hx-v3-copy') - numeric(b, 'data-hx-v3-copy'));
    const steps = [...root.querySelectorAll('[data-hx-v3-step]')];
    const count = Math.min(products.length, copies.length);
    if (!stage || !pin || !count) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const cleanups = [];
    const rocks = [...root.querySelectorAll('[data-hx-v3-rock]')].map((element, index) => ({
      element,
      x: clamp(numeric(element, 'data-x'), -250, 250),
      y: clamp(numeric(element, 'data-y'), -250, 250),
      spin: clamp(numeric(element, 'data-spin'), -720, 720),
      depth: 0.45 + (index % 4) * 0.2
    }));
    let frame = 0;
    let visible = false;
    let active = -1;
    let disposed = false;
    let pointerX = 0;
    let pointerY = 0;

    const listen = (target, event, handler, options) => {
      target.addEventListener(event, handler, options);
      cleanups.push(() => target.removeEventListener(event, handler, options));
    };
    const cancelFrame = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    };
    const syncMotionState = () => {
      const inView = visible && root.isConnected && !disposed;
      const motionActive = inView && !document.hidden && !reducedMotion.matches;
      root.classList.toggle('is-v3-in-view', inView);
      // CSS supplies the slow product motion; JavaScript only wakes for input.
      root.classList.toggle('is-v3-motion-active', motionActive);
      if (!motionActive) cancelFrame();
    };
    const measureVisibility = () => {
      const bounds = stage.getBoundingClientRect();
      visible = bounds.bottom > 0 && bounds.top < window.innerHeight &&
        bounds.right > 0 && bounds.left < window.innerWidth;
      syncMotionState();
    };
    const setActive = (index, force = false) => {
      if (active === index && !force) return;
      active = index;
      root.dataset.hxV3Active = String(index);
      const family = products[index]?.dataset.hxV3Family;
      if (family) root.dataset.hxV3Family = family;
      else delete root.dataset.hxV3Family;
      [products, copies].forEach(elements => elements.forEach((element, itemIndex) => {
        const selected = itemIndex === index;
        const accessible = reducedMotion.matches || selected;
        element.classList.toggle('is-active', selected);
        element.inert = !accessible;
        element.setAttribute('aria-hidden', String(!accessible));
      }));
      steps.forEach(step => {
        const selected = numeric(step, 'data-hx-v3-step') === index;
        step.classList.toggle('is-active', selected);
        if (selected) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    };
    const transform = (element, relative, opacity, copy = false) => {
      element.style.setProperty('--hx-v3-item-progress', relative.toFixed(4));
      element.style.setProperty('--hx-v3-item-opacity', opacity.toFixed(4));
      element.style.setProperty('--hx-v3-item-y', (-relative * 22).toFixed(3) + (copy ? 'px' : 'vh'));
      element.style.setProperty('--hx-v3-item-scale', (copy ? 1 : 1 - Math.abs(relative) * 0.17).toFixed(4));
      element.style.setProperty('--hx-v3-item-rotate', (copy ? 0 : -relative * 13).toFixed(3) + 'deg');
    };
    const resetMotion = () => {
      pointerX = 0;
      pointerY = 0;
      root.style.setProperty('--hx-v3-progress', '0');
      root.style.setProperty('--hx-v3-pointer-x', '0px');
      root.style.setProperty('--hx-v3-pointer-y', '0px');
      products.forEach(element => transform(element, 0, 1));
      copies.forEach(element => transform(element, 0, 1, true));
      rocks.forEach(({ element }) => {
        element.style.setProperty('--hx-v3-rock-x', '0px');
        element.style.setProperty('--hx-v3-rock-y', '0px');
        element.style.setProperty('--hx-v3-rock-rotate', '0deg');
      });
    };
    const measure = () => {
      const bounds = stage.getBoundingClientRect();
      // The pin may be shorter than the viewport below the native Shopify header.
      const distance = Math.max(1, bounds.height - pin.getBoundingClientRect().height);
      return { top: bounds.top, distance };
    };
    const render = () => {
      frame = 0;
      if (disposed || !visible || !root.isConnected || document.hidden || reducedMotion.matches) return;
      const { top, distance } = measure();
      const progress = clamp(-top / distance);
      const rawPhase = progress * Math.max(1, count - 1);
      const segment = Math.min(count - 2, Math.floor(rawPhase));
      // Brief still moments at each chapter give its text and CTA time to settle.
      const phase = count === 1 ? 0 : segment + smooth(clamp((rawPhase - segment - 0.18) / 0.64));
      root.style.setProperty('--hx-v3-progress', progress.toFixed(5));
      root.style.setProperty('--hx-v3-pointer-x', (pointerX * 10).toFixed(2) + 'px');
      root.style.setProperty('--hx-v3-pointer-y', (pointerY * 7).toFixed(2) + 'px');
      setActive(Math.round(phase));
      products.forEach((element, index) => {
        const relative = clamp(phase - index, -1, 1);
        transform(element, relative, smooth(1 - Math.abs(relative)));
      });
      copies.forEach((element, index) => {
        const relative = clamp(phase - index, -1, 1);
        // The accessible chapter always stays legible, even when scrolling stops mid-transition.
        const opacity = index === active ? 0.8 + 0.2 * smooth(1 - Math.abs(relative)) : 0;
        transform(element, relative, opacity, true);
      });
      const flight = smooth(progress);
      const width = window.innerWidth;
      const height = window.innerHeight;
      rocks.forEach(({ element, x, y, spin, depth }) => {
        element.style.setProperty('--hx-v3-rock-x', (x * width / 100 * flight + pointerX * 14 * depth).toFixed(2) + 'px');
        element.style.setProperty('--hx-v3-rock-y', (y * height / 100 * flight + pointerY * 10 * depth).toFixed(2) + 'px');
        element.style.setProperty('--hx-v3-rock-rotate', (spin * flight).toFixed(2) + 'deg');
      });
    };
    const requestFrame = () => {
      if (!frame && visible && root.isConnected && !disposed && !document.hidden && !reducedMotion.matches) {
        frame = window.requestAnimationFrame(render);
      }
    };
    const goToStep = index => {
      index = clamp(index, 0, count - 1);
      if (reducedMotion.matches) {
        setActive(index);
        copies[index].scrollIntoView({ behavior: 'instant', block: 'center' });
        return;
      }
      const bounds = stage.getBoundingClientRect();
      // Chapter destinations remain stable while the native menu slides away.
      const distance = Math.max(1, bounds.height - Math.max(window.innerHeight, pin.getBoundingClientRect().height));
      const target = window.scrollY + bounds.top + distance * index / Math.max(1, count - 1);
      window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    };
    steps.forEach((step, index) => {
      listen(step, 'click', () => goToStep(numeric(step, 'data-hx-v3-step')));
      listen(step, 'keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % steps.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + steps.length) % steps.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = steps.length - 1;
        else return;
        event.preventDefault();
        steps[next].focus({ preventScroll: true });
        goToStep(numeric(steps[next], 'data-hx-v3-step'));
      });
    });
    const hasIntersectionObserver = typeof window.IntersectionObserver === 'function';
    listen(window, 'scroll', () => {
      // Older browsers still pause offscreen effects without a polling loop.
      if (!hasIntersectionObserver) measureVisibility();
      requestFrame();
    }, { passive: true });
    listen(window, 'resize', () => {
      measureVisibility();
      requestFrame();
    }, { passive: true });
    const nativeHeader = document.querySelector('.section-header');
    if (nativeHeader) listen(nativeHeader, 'transitionend', requestFrame);
    listen(document, 'visibilitychange', () => {
      measureVisibility();
      requestFrame();
    });
    listen(pin, 'pointermove', event => {
      if (reducedMotion.matches || !finePointer.matches || event.pointerType === 'touch') return;
      pointerX = clamp(event.clientX / Math.max(1, window.innerWidth) * 2 - 1, -1, 1);
      pointerY = clamp(event.clientY / Math.max(1, window.innerHeight) * 2 - 1, -1, 1);
      requestFrame();
    }, { passive: true });
    listen(pin, 'pointerleave', () => {
      pointerX = 0;
      pointerY = 0;
      requestFrame();
    });
    listen(finePointer, 'change', () => {
      if (!finePointer.matches) {
        pointerX = 0;
        pointerY = 0;
      }
      requestFrame();
    });
    const applyMotionPreference = () => {
      cancelFrame();
      root.classList.toggle('is-v3-reduced', reducedMotion.matches);
      if (reducedMotion.matches) resetMotion();
      setActive(Math.max(0, active), true);
      measureVisibility();
      requestFrame();
    };
    listen(reducedMotion, 'change', applyMotionPreference);
    if (hasIntersectionObserver) {
      const observer = new window.IntersectionObserver(entries => {
        if (disposed) return;
        visible = entries.some(entry => entry.isIntersecting);
        syncMotionState();
        requestFrame();
      }, { threshold: 0, rootMargin: '0px' });
      observer.observe(stage);
      cleanups.push(() => observer.disconnect());
    }

    root.classList.add('is-v3-ready');
    applyMotionPreference();
    instances.set(root, () => {
      disposed = true;
      cancelFrame();
      cleanups.forEach(cleanup => cleanup());
      root.classList.remove('is-v3-ready', 'is-v3-in-view', 'is-v3-reduced', 'is-v3-motion-active');
      delete root.dataset.hxV3Family;
      instances.delete(root);
    });
  };

  const scan = (scope = document) => {
    if (scope.matches?.('[data-hx-v3]')) init(scope);
    scope.querySelectorAll('[data-hx-v3]').forEach(init);
  };
  window[controllerKey] = { scan };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-hx-v3]')) instances.get(event.target)?.();
    event.target.querySelectorAll('[data-hx-v3]').forEach(root => instances.get(root)?.());
  });
})();
